import { motion } from 'framer-motion'
import {
  PaintBrushIcon,
  EnvelopeIcon,
  ChatBubbleLeftEllipsisIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  MapIcon,
  ChartBarIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useAppStore } from '../store/useAppStore'
import type { ServiceType } from '../types/database'
import { clsx } from 'clsx'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

interface ServiceCard {
  type: ServiceType
  name: string
  description: string
  icon: any
  features: string[]
  color: string
  bgColor: string
}

const services: ServiceCard[] = [
  {
    type: 'enterprise_intelligence',
    name: 'Enterprise Intelligence',
    description: 'AI-powered business performance intelligence and analytics platform.',
    icon: ChartBarIcon,
    features: [
      'Real-time KPI tracking',
      'AI-powered insights',
      'Automated reporting',
      'Data aggregation from multiple sources',
    ],
    color: 'text-primary-600',
    bgColor: 'bg-primary-100',
  },
  {
    type: 'web_design',
    name: 'Web Design',
    description: 'Professional website design and development with modern aesthetics.',
    icon: PaintBrushIcon,
    features: [
      'Custom responsive design',
      'SEO optimization',
      'Performance optimization',
      'CMS integration',
    ],
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  {
    type: 'marketing',
    name: 'Marketing Automation',
    description: 'Multi-channel marketing across email, WhatsApp, Telegram, and social media.',
    icon: EnvelopeIcon,
    features: [
      'Email outreach automation',
      'WhatsApp campaigns',
      'Telegram broadcasts',
      'Social media management',
    ],
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  {
    type: 'ai_assistant',
    name: 'AI Chat & Voice Agent',
    description: 'Intelligent conversational AI trained on your business capabilities.',
    icon: ChatBubbleLeftEllipsisIcon,
    features: [
      '24/7 customer support',
      'Multi-channel deployment',
      'Voice assistance',
      'Custom training on your services',
    ],
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  {
    type: 'crm',
    name: 'CRM System',
    description: 'Complete customer relationship management and pipeline tracking.',
    icon: UserGroupIcon,
    features: [
      'Contact management',
      'Deal pipeline',
      'Activity tracking',
      'Automated follow-ups',
    ],
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  {
    type: 'branding',
    name: 'Branding Services',
    description: 'Professional brand identity, logo design, and brand guidelines.',
    icon: PaintBrushIcon,
    features: [
      'Logo design',
      'Brand identity',
      'Style guides',
      'Marketing collateral',
    ],
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
  },
  {
    type: 'vulnerability_assessment',
    name: 'Vulnerability Assessment',
    description: 'Comprehensive security scanning and remediation services.',
    icon: ShieldCheckIcon,
    features: [
      'Security scanning',
      'Vulnerability reports',
      'Remediation guidance',
      'Compliance checks',
    ],
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  {
    type: 'mobile_app',
    name: 'Mobile App Development',
    description: 'Cross-platform mobile app development for iOS and Android.',
    icon: DevicePhoneMobileIcon,
    features: [
      'iOS & Android apps',
      'Cross-platform development',
      'App Store submission',
      'Push notifications',
    ],
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  {
    type: 'google_maps',
    name: 'Google Maps Visibility',
    description: 'Optimize your Google Business Profile for maximum local visibility.',
    icon: MapIcon,
    features: [
      'Profile optimization',
      'Review management',
      'Local SEO',
      'Photo optimization',
    ],
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
]

export const Services = () => {
  const { services: userServices, setActiveServiceType, activeServiceType } = useAppStore()

  const handleServiceSelect = (type: ServiceType) => {
    setActiveServiceType(type)
  }

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={item}>
        <h1 className="text-2xl font-bold text-metallic-900">Our Services</h1>
        <p className="text-metallic-500 mt-1">
          Comprehensive solutions to power your business growth.
        </p>
      </motion.div>

      {/* Services Grid */}
      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => {
          const isActive = activeServiceType === service.type
          const isSubscribed = userServices.some((s) => s.type === service.type)

          return (
            <Card
              key={service.type}
              hover
              className={clsx(
                'relative overflow-hidden transition-all duration-300',
                isActive && 'ring-2 ring-primary-500',
                isSubscribed && 'border-success-200'
              )}
              onClick={() => handleServiceSelect(service.type)}
            >
              {/* Active indicator */}
              {isSubscribed && (
                <div className="absolute top-4 right-4">
                  <Badge variant="success" dot>
                    Active
                  </Badge>
                </div>
              )}

              {/* Icon */}
              <div className={clsx('w-14 h-14 rounded-2xl flex items-center justify-center mb-4', service.bgColor)}>
                <service.icon className={clsx('w-7 h-7', service.color)} />
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-metallic-900 mb-2">{service.name}</h3>
              <p className="text-sm text-metallic-500 mb-4">{service.description}</p>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-metallic-600">
                    <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Action */}
              <Button
                variant={isSubscribed ? 'secondary' : 'primary'}
                size="sm"
                className="w-full"
                rightIcon={<ArrowRightIcon className="w-4 h-4" />}
              >
                {isSubscribed ? 'Manage Service' : 'Get Started'}
              </Button>
            </Card>
          )
        })}
      </motion.div>

      {/* Service Detail Panel */}
      {activeServiceType && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <Card className="bg-gradient-to-r from-primary-50 to-white border-primary-200">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-metallic-900">
                  {services.find((s) => s.type === activeServiceType)?.name}
                </h3>
                <p className="text-metallic-600 mt-2 max-w-2xl">
                  {services.find((s) => s.type === activeServiceType)?.description}
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => setActiveServiceType(null)}
              >
                Close
              </Button>
            </div>
          </Card>
        </motion.div>
      )}
    </motion.div>
  )
}
